/**
 * PhotoSwipe Gallery Handler
 * Clean optimized version with mobile zoom button functionality
 */
class PhotoSwipeGallery {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.gallerySelector = `#MediaGallery-${sectionId}`;
    this.itemSelector = '.product__media-item[data-pswp-src]';
    this.zoomButtonSelector = '.product-gallery__zoom-button';
    this.isInitialized = false;
    this.clickHandlersAttached = false;
    this.clickHandler = null;
    this.zoomButtonHandler = null;
    
    // Bind methods
    this.handleImageClick = this.handleImageClick.bind(this);
    this.handleZoomButtonClick = this.handleZoomButtonClick.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);
    
    this.init();
  }

  init() {
    if (typeof PhotoSwipe === 'undefined' || typeof PhotoSwipeLightbox === 'undefined') {
      return;
    }

    this.attachEventListeners();
    this.attachClickHandlers();
    this.attachZoomButtonHandler();
    this.isInitialized = true;
  }

  // Check if device is mobile (screen width)
  isMobile() {
    return window.innerWidth < 990;
  }

  // Check if current slide is a video
  isCurrentSlideVideo() {
    // Get ALL media items (not just ones with data-pswp-src)
    const allMediaItems = document.querySelectorAll(`${this.gallerySelector} .product__media-item`);
    
    const visibleItems = Array.from(allMediaItems).filter(item => {
      const isHidden = item.classList.contains('item-hidden') || 
                       item.hidden || 
                       item.hasAttribute('hidden') ||
                       item.style.display === 'none';
      return !isHidden;
    });
    
    // Find the currently visible/active item in viewport
    let currentItem = null;
    let maxVisibleArea = 0;
    
    visibleItems.forEach((item) => {
      const rect = item.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const visibleArea = visibleWidth * visibleHeight;
      
      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        currentItem = item;
      }
    });
    
    if (!currentItem) return false;
    
    // Check if current item contains video
    const hasVideo = currentItem.querySelector('video') !== null ||
                     currentItem.querySelector('iframe') !== null ||
                     currentItem.querySelector('deferred-media') !== null ||
                     currentItem.classList.contains('product__media-item--full');
    
    return hasVideo;
  }

  updateZoomButtonVisibility() {
    const zoomButton = document.querySelector(this.zoomButtonSelector);
    if (!zoomButton) return;
    
    if (this.isCurrentSlideVideo()) {
      zoomButton.style.display = 'none';
    } else {
      zoomButton.style.display = '';
    }
  }

  registerLightboxUi(pswpInstance) {
    // Close button
    pswpInstance.ui.registerElement({
      name: "close-button",
      className: "circle-button circle-button--xl hover:animate-icon-block",
      ariaLabel: "Close",
      order: 2,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
      onClick: () => pswpInstance.close()
    });

    // Previous and Next buttons (only if more than 1 image)
    if (pswpInstance.options.dataSource.length > 1) {
      // Previous button
      pswpInstance.ui.registerElement({
        name: "previous-button",
        className: "circle-button hover:animate-icon-inline",
        ariaLabel: "Previous",
        order: 1,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="M11 1 3 9l8 8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => pswpInstance.prev()
      });

      // Next button
      pswpInstance.ui.registerElement({
        name: "next-button",
        className: "circle-button hover:animate-icon-inline",
        ariaLabel: "Next",
        order: 3,
        isButton: true,
        html: `
          <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon icon--direction-aware" viewBox="0 0 16 18">
            <path d="m5 17 8-8-8-8" stroke="currentColor" stroke-linecap="square"/>
          </svg>
        `,
        onClick: () => pswpInstance.next()
      });
    }
  }

  getCurrentActiveIndex() {
    const items = document.querySelectorAll(`${this.gallerySelector} ${this.itemSelector}`);
    
    const visibleItems = Array.from(items).filter(item => {
      const isHidden = item.classList.contains('item-hidden') || 
                       item.hidden || 
                       item.hasAttribute('hidden') ||
                       item.style.display === 'none';
      return !isHidden;
    });
    
    if (visibleItems.length === 0) return 0;
    
    let mostVisibleIndex = 0;
    let maxVisibleArea = 0;
    
    visibleItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      const visibleArea = visibleWidth * visibleHeight;
      
      if (visibleArea > maxVisibleArea) {
        maxVisibleArea = visibleArea;
        mostVisibleIndex = index;
      }
    });
    
    return mostVisibleIndex;
  }

  openPhotoSwipe(index = null) {
    const items = document.querySelectorAll(`${this.gallerySelector} ${this.itemSelector}`);
    const galleryItems = [];
    const visibleItems = [];
    
    items.forEach((item, originalIndex) => {
      const isHidden = item.classList.contains('item-hidden') || 
                       item.hidden || 
                       item.hasAttribute('hidden') ||
                       item.style.display === 'none';
      
      if (!isHidden) {
        const thumbImg = item.querySelector('img');
        galleryItems.push({
          src: item.dataset.pswpSrc,
          width: parseInt(item.dataset.pswpWidth) || 1200,
          height: parseInt(item.dataset.pswpHeight) || 1200,
          alt: thumbImg?.alt || '',
          msrc: thumbImg?.src || item.dataset.pswpSrc,
          originalIndex: originalIndex
        });
        visibleItems.push(item);
      }
    });
    
    if (galleryItems.length === 0) return;
    
    let startIndex = 0;
    
    if (index !== null) {
      startIndex = Math.max(0, Math.min(index, galleryItems.length - 1));
    } else {
      startIndex = this.getCurrentActiveIndex();
    }
    
    startIndex = Math.max(0, Math.min(startIndex, galleryItems.length - 1));
    
    const pswp = new PhotoSwipe({
      dataSource: galleryItems,
      index: startIndex,
      showHideAnimationType: 'zoom',
      showAnimationDuration: 333,
      hideAnimationDuration: 333,
      bgOpacity: 0.9,
      easing: 'cubic-bezier(.4,0,.22,1)',
      trapFocus: false,
      returnFocus: false,
      getThumbBoundsFn: (index) => {
        const thumbnail = visibleItems[index];
        if (!thumbnail) return null;
        
        const thumbImg = thumbnail.querySelector('img');
        if (!thumbImg) return null;
        
        const rect = thumbImg.getBoundingClientRect();
        const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
        const pageXScroll = window.pageXOffset || document.documentElement.scrollLeft;
        
        return {
          x: rect.left + pageXScroll,
          y: rect.top + pageYScroll,
          w: rect.width
        };
      },
      closeTitle: '',
      nextTitle: '',
      prevTitle: '',
      zoomTitle: '',
      arrowPrevTitle: '',
      arrowNextTitle: '',
      closeEl: false,
      captionEl: false,
      fullscreenEl: false,
      zoomEl: false,
      shareEl: false,
      counterEl: false,
      arrowEl: false,
      preloaderEl: true
    });
    
    pswp.on('uiRegister', () => {
      this.registerLightboxUi(pswp);
    });
    
    pswp.on('change', () => {});
    pswp.init();
    return pswp;
  }

handleImageClick(e) {
    // 1. Block mobile click to allow native behavior (already in your code)
    if (this.isMobile()) {
      return;
    }
    
    const clickedItem = e.currentTarget;

    // 2. NEW: Check if the clicked item is a video/media item
    const isVideo = clickedItem.querySelector('video, iframe, deferred-media') !== null || 
                    clickedItem.classList.contains('product__media-item--full');
    
    if (isVideo) {
      // If it's a video, we do nothing and let the theme's video player handle the click
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    const items = document.querySelectorAll(`${this.gallerySelector} ${this.itemSelector}`);
    const visibleItems = Array.from(items).filter(item => !item.hidden && !item.hasAttribute('hidden'));
    const index = visibleItems.indexOf(clickedItem);
    
    if (index >= 0) {
      this.openPhotoSwipe(index);
    }
  }

  handleZoomButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.openPhotoSwipe();
  }

  attachClickHandlers() {
    const gallery = document.querySelector(this.gallerySelector);
    if (!gallery) return;

    if (this.clickHandlersAttached) {
      gallery.removeEventListener('click', this.clickHandler);
      this.clickHandlersAttached = false;
    }

this.clickHandler = (e) => {
      const clickedItem = e.target.closest(this.itemSelector);
      if (!clickedItem) return;

      // Check if the clicked slide is a video
      const isVideo = clickedItem.querySelector('video, iframe, deferred-media') !== null || 
                      clickedItem.classList.contains('product__media-item--full');

      // If it's a video, DON'T prevent default and DON'T open PhotoSwipe
      if (isVideo) return;

      this.handleImageClick({ 
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation(),
        currentTarget: clickedItem 
      });
    };
    
    gallery.addEventListener('click', this.clickHandler);
    this.clickHandlersAttached = true;
  }

  attachZoomButtonHandler() {
    const gallery = document.querySelector(this.gallerySelector);
    if (!gallery) return;

    if (this.zoomButtonHandler) {
      const existingZoomButton = gallery.querySelector(this.zoomButtonSelector);
      if (existingZoomButton) {
        existingZoomButton.removeEventListener('click', this.zoomButtonHandler);
      }
    }

    this.zoomButtonHandler = (e) => {
      this.handleZoomButtonClick(e);
    };

    const zoomButton = gallery.querySelector(this.zoomButtonSelector);
    if (zoomButton) {
      zoomButton.addEventListener('click', this.zoomButtonHandler);
    }
  }

  handleVariantChange() {
    clearTimeout(this.variantChangeTimer);
    this.variantChangeTimer = setTimeout(() => {
      this.attachClickHandlers();
      this.attachZoomButtonHandler();
      this.updateZoomButtonVisibility();
    }, 300);
  }

  handleFormChange() {
    clearTimeout(this.formChangeTimer);
    this.formChangeTimer = setTimeout(() => {
      this.attachClickHandlers();
      this.attachZoomButtonHandler();
    }, 300);
  }

  attachEventListeners() {
    document.addEventListener('variant:change', this.handleVariantChange);
    
    const productForm = document.querySelector('.product-info form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('change', this.handleFormChange);
    }

    // Slider scroll listener for zoom button visibility
    const sliderGallery = document.querySelector(`#Slider-Gallery-${this.sectionId}`);
    if (sliderGallery) {
      sliderGallery.addEventListener('scroll', () => {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          this.updateZoomButtonVisibility();
        }, 100);
      });
    }

    // Initial check
    this.updateZoomButtonVisibility();

    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.attachClickHandlers();
        this.attachZoomButtonHandler();
      }, 250);
    };
    
    window.addEventListener('resize', this.resizeHandler);
  }

  destroy() {
    document.removeEventListener('variant:change', this.handleVariantChange);
    
    const productForm = document.querySelector('.product-info form[action*="/cart/add"]');
    if (productForm) {
      productForm.removeEventListener('change', this.handleFormChange);
    }
    
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    const gallery = document.querySelector(this.gallerySelector);
    if (gallery) {
      if (this.clickHandler) {
        gallery.removeEventListener('click', this.clickHandler);
      }
      
      const zoomButton = gallery.querySelector(this.zoomButtonSelector);
      if (zoomButton && this.zoomButtonHandler) {
        zoomButton.removeEventListener('click', this.zoomButtonHandler);
      }
    }
    
    clearTimeout(this.variantChangeTimer);
    clearTimeout(this.formChangeTimer);
    clearTimeout(this.resizeTimer);
    
    this.clickHandler = null;
    this.zoomButtonHandler = null;
    this.clickHandlersAttached = false;
    this.isInitialized = false;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  if (typeof PhotoSwipe === 'undefined' || typeof PhotoSwipeLightbox === 'undefined') {
    return;
  }

  const mediaGallery = document.querySelector('[id^="MediaGallery-"]');
  if (mediaGallery) {
    const sectionId = mediaGallery.id.replace('MediaGallery-', '');
    window.photoSwipeGallery = new PhotoSwipeGallery(sectionId);
  }
});

window.PhotoSwipeGallery = PhotoSwipeGallery;